import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Bug, Upload, X, Loader2, CheckCircle } from 'lucide-react';

const BugReportModal = ({ open, onOpenChange }) => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    title: '',
    description: '',
    steps_to_reproduce: '',
    expected_behavior: '',
    severity: 'medium',
  });

  const resetForm = () => {
    setForm({
      title: '',
      description: '',
      steps_to_reproduce: '',
      expected_behavior: '',
      severity: 'medium',
    });
    setScreenshot(null);
    setScreenshotPreview(null);
    setSubmitted(false);
  };

  const handleClose = (value) => {
    if (!value) resetForm();
    onOpenChange(value);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La capture d\'écran doit faire moins de 5 Mo');
      return;
    }
    setScreenshot(file);
    setScreenshotPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Le titre et la description sont obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      const pageUrl = window.location.pathname;
      const browserInfo = navigator.userAgent;

      if (screenshot) {
        const formData = new FormData();
        formData.append('title', form.title);
        formData.append('description', form.description);
        if (form.steps_to_reproduce) formData.append('steps_to_reproduce', form.steps_to_reproduce);
        if (form.expected_behavior) formData.append('expected_behavior', form.expected_behavior);
        formData.append('severity', form.severity);
        formData.append('page_url', pageUrl);
        formData.append('browser_info', browserInfo);
        formData.append('screenshot', screenshot);

        await api.post('/bug-reports/with-screenshot', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post('/bug-reports', {
          title: form.title,
          description: form.description,
          steps_to_reproduce: form.steps_to_reproduce || null,
          expected_behavior: form.expected_behavior || null,
          severity: form.severity,
          page_url: pageUrl,
          browser_info: browserInfo,
        });
      }

      setSubmitted(true);
      toast.success('Rapport de bug envoyé avec succès');
      setTimeout(() => handleClose(false), 1500);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du rapport');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Merci !</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center">
              Votre rapport de bug a été envoyé. Notre équipe l'examinera rapidement.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-white">
            <Bug className="w-5 h-5 text-rose-500" />
            Signaler un bug
          </DialogTitle>
          <DialogDescription>
            Aidez-nous à améliorer la plateforme en signalant les problèmes rencontrés.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bug-title">Titre <span className="text-rose-500">*</span></Label>
            <Input
              id="bug-title"
              placeholder="Brève description du problème"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bug-description">Description <span className="text-rose-500">*</span></Label>
            <Textarea
              id="bug-description"
              placeholder="Que s'est-il passé ? Soyez le plus détaillé possible."
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bug-steps">Étapes pour reproduire</Label>
            <Textarea
              id="bug-steps"
              placeholder="1. Aller sur...&#10;2. Cliquer sur...&#10;3. Observer..."
              value={form.steps_to_reproduce}
              onChange={(e) => setForm({ ...form, steps_to_reproduce: e.target.value })}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bug-expected">Comportement attendu</Label>
            <Textarea
              id="bug-expected"
              placeholder="Que deviez-vous obtenir normalement ?"
              value={form.expected_behavior}
              onChange={(e) => setForm({ ...form, expected_behavior: e.target.value })}
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>Sévérité</Label>
            <Select value={form.severity} onValueChange={(val) => setForm({ ...form, severity: val })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Basse - Problème visuel mineur</SelectItem>
                <SelectItem value="medium">Moyenne - Fonctionnalité partiellement cassée</SelectItem>
                <SelectItem value="high">Haute - Fonctionnalité complètement cassée</SelectItem>
                <SelectItem value="critical">Critique - Crash / perte de données</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Capture d'écran (optionnel)</Label>
            {screenshotPreview ? (
              <div className="relative rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                <img
                  src={screenshotPreview}
                  alt="Aperçu de la capture"
                  className="w-full max-h-40 object-cover"
                />
                <button
                  type="button"
                  onClick={() => {
                    setScreenshot(null);
                    setScreenshotPreview(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="absolute top-2 right-2 p-1 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg p-4 text-center hover:border-violet-400 dark:hover:border-violet-500 hover:bg-violet-50/50 dark:hover:bg-violet-900/10 transition-colors cursor-pointer"
              >
                <Upload className="w-5 h-5 mx-auto mb-1 text-slate-400" />
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  Cliquer pour ajouter une capture d'écran
                </span>
                <span className="text-xs text-slate-400 block mt-0.5">PNG, JPG jusqu'à 5 Mo</span>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={submitting}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={submitting || !form.title.trim() || !form.description.trim()}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Bug className="w-4 h-4 mr-2" />
                  Envoyer le rapport
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BugReportModal;
